import type { ComponentProps, FC, ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';
import type { SelectChangeEvent, SxProps, Theme } from '@mui/material';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select as MuiSelect,
  TextField,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { bindDialog, usePopupState } from 'material-ui-popup-state/hooks';
import { get as getByPath } from 'lodash-es';
import CustomScrollbarListbox from './CustomScrollbarListbox';

export interface Option {
  value: string | number;
  label: string;
}

export interface SelectProps {
  name: string;
  options: Option[];
  label?: string;
  inputLabel?: string;
  placeholder?: string;
  helperText?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  handleChange?: (event: SelectChangeEvent<unknown>) => void;
  PaperProps?: object;
  sx?: SxProps<Theme>;
  restProps?: ComponentProps<typeof MuiSelect>;
  autoFocus?: boolean;
  clearable?: boolean;
  loading?: boolean;
  loadMoreRef?: (node?: Element | null) => void;
  confirmation?: {
    title?: string;
    bodyText?: string;
    cancelButtonTitle?: string;
    submitButtonTitle?: string;
    onSubmit?: () => void;
  };
  onClose?: () => void;
  asAutocomplete?: boolean;
  dataTestId?: string;
  renderOption?: (option: Option) => ReactNode;
}

const Select: FC<SelectProps> = (
  {
    name,
    options,
    label,
    inputLabel,
    required,
    disabled,
    handleChange,
    sx,
    PaperProps,
    autoFocus,
    confirmation,
    placeholder,
    clearable,
    loading,
    loadMoreRef,
    asAutocomplete,
    helperText,
    dataTestId,
    renderOption,
    ...restProps
  }) => {
  const [fieldValue, setFieldValue] = useState<number | string | undefined>('');
  const popupState = usePopupState({
    variant: 'dialog',
    popupId: `ConfirmationDialog_${name}`,
  });

  const { control, formState, trigger, setValue, watch } =
    useFormContext<FieldValues>();
  const currentValue = watch(name) as string;
  const error = getByPath(formState.errors, name);

  useEffect(() => {
    if (asAutocomplete && currentValue && typeof currentValue === 'object') {
      const valueToSet =
        (currentValue as { value?: string | number }).value ?? currentValue;
      setValue(name, valueToSet, { shouldValidate: true });
    }
  }, [asAutocomplete, currentValue, name, setValue]);

  const handleOpenConfirmation = useCallback(
    (value: string | number | undefined) => {
      setFieldValue(value);
      popupState.open();
    },
    [setFieldValue, popupState],
  );

  const handleApproveConfirmation = useCallback(() => {
    confirmation?.onSubmit?.();
    setValue(name, fieldValue);
    setFieldValue('');
    popupState.close();
  }, [setValue, setFieldValue, name, popupState, confirmation, fieldValue]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const { value, onChange } = field;

        if (asAutocomplete) {
          return (
            <Box
              sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}
            >
              {label && !inputLabel && (
                <Typography variant="subtitle2" mb={1}>
                  {label}
                  {required && '*'}
                </Typography>
              )}
              <FormControl sx={{ flex: 1 }}>
                <Autocomplete
                  data-testid={dataTestId}
                  value={
                    value
                      ? (options.find((option) => {
                        const optionValue = option.value;
                        const currentValue =
                          typeof value === 'object' && value !== null
                            ? ((value as { value?: string | number }).value ??
                              value)
                            : value;

                        return String(optionValue) === String(currentValue);
                      }) ?? null)
                      : null
                  }
                  onChange={(_, newValue) => {
                    const rawValue = newValue?.value;
                    onChange(rawValue);
                    trigger(name).catch(console.error);
                    handleChange &&
                    handleChange({
                      target: { value: rawValue ?? '' },
                    } as SelectChangeEvent<unknown>);
                  }}
                  options={options}
                  getOptionLabel={(option) => option.label}
                  renderOption={
                    renderOption
                      ? (props, option) => (
                          <li {...props} key={String(option.value)}>
                            {renderOption(option)}
                          </li>
                        )
                      : undefined
                  }
                  isOptionEqualToValue={(option, value) => {
                    const optionValue = option.value;
                    const currentValue =
                      typeof value === 'object' && value !== null
                        ? ((value as { value?: string | number }).value ??
                          value)
                        : value;

                    return String(optionValue) === String(currentValue);
                  }}
                  disabled={disabled}
                  loading={loading}
                  noOptionsText="No options"
                  ListboxComponent={
                    options.length > 4 ? CustomScrollbarListbox : undefined
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={inputLabel}
                      placeholder={placeholder}
                      error={!!error}
                      required={required}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      InputLabelProps={{
                        sx: {
                          '&.MuiInputLabel-root.MuiFormLabel-root': {
                            top: -6,
                            color: error ? 'error.main' : undefined,
                          },
                        },
                      }}
                    />
                  )}
                  ListboxProps={{
                    onScroll: (event) => {
                      const listboxNode = event.currentTarget;
                      if (
                        listboxNode.scrollTop + listboxNode.clientHeight >=
                        listboxNode.scrollHeight - 20
                      ) {
                        loadMoreRef?.(listboxNode);
                      }
                    },
                  }}
                  sx={sx}
                  slotProps={{
                    popper: {
                      sx: {
                        '& .MuiAutocomplete-option': {
                          paddingX: 2,
                          paddingY: 1,
                          transition: 'background-color 0.2s',
                          cursor: 'pointer',
                        },
                        '& .MuiAutocomplete-option:hover': {
                          backgroundColor: (theme) =>
                            theme.palette.action.hover,
                        },
                        '& .MuiAutocomplete-noOptions': {
                          fontSize: 14,
                          color: (theme) => theme.palette.text.disabled,
                        },
                      },
                    },
                  }}
                />
                {error?.message && (
                  <Typography
                    variant="subtitle1"
                    fontSize={12}
                    sx={{ mt: '3px' }}
                    color="error"
                  >
                    {error.message as string}
                  </Typography>
                )}
                {!error?.message && helperText && (
                  <Typography
                    variant="subtitle1"
                    fontSize={12}
                    sx={{ mt: '3px' }}
                    color="text.secondary"
                  >
                    {helperText}
                  </Typography>
                )}
              </FormControl>
            </Box>
          );
        }

        return (
          <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
            {label && !inputLabel && (
              <Typography variant="subtitle2" mb={1}>
                {label}
                {required && '*'}
              </Typography>
            )}
            <FormControl sx={{ flex: 1 }}>
              {!label && inputLabel && (
                <InputLabel
                  id={`${inputLabel}_select`}
                  sx={{ color: error ? 'error.main' : undefined }}
                >
                  {inputLabel ? `${inputLabel}${required ? '*' : ''}` : null}
                </InputLabel>
              )}
              <MuiSelect
                data-testid={dataTestId}
                autoFocus={autoFocus}
                labelId={inputLabel ? `${inputLabel}_select` : undefined}
                {...field}
                fullWidth
                IconComponent={clearable && value ? () => null : undefined}
                input={
                  <OutlinedInput
                    placeholder={placeholder ?? inputLabel}
                    label={
                      inputLabel ? `${inputLabel}${required ? '*' : ''}` : null
                    }
                    sx={{
                      whiteSpace: 'normal',
                      '& .MuiInputLabel-root': {
                        color: error ? 'error.main' : undefined,
                      },
                    }}
                    endAdornment={
                      <>
                        {clearable && value && (
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              onChange('');
                              trigger(name).catch(console.error);
                              handleChange &&
                              handleChange({
                                target: { value: '' },
                              } as SelectChangeEvent<unknown>);
                            }}
                            size="small"
                            sx={{
                              visibility: value ? 'visible' : 'hidden',
                              backgroundColor: 'white',
                              mr: -1,
                            }}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        )}
                        {loading && (
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                        )}
                      </>
                    }
                  />
                }
                MenuProps={{
                  PaperProps: {
                    ...PaperProps,
                    sx: {
                      maxHeight: 240,
                      maxWidth: '60vw',
                    },
                  },
                  MenuListProps: {
                    component:
                      options.length > 4 ? CustomScrollbarListbox : undefined,
                    sx: {
                      '&.MuiAutocomplete-option:hover': {
                        backgroundColor: (theme) => theme.palette.action.hover,
                      },
                      '&.MuiAutocomplete-option': {
                        // Optionally, you can add transition for smoothness
                        transition: 'background-color 0.2s',
                        cursor: 'pointer',
                      },
                    },
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
                error={!!error}
                disabled={disabled}
                sx={{
                  ...sx,
                  ...(!value && {
                    '& .MuiSelect-nativeInput': {
                      top: 0,
                      pl: 1.7,
                      border: 'none',
                    },
                    '&.Mui-focused .MuiSelect-nativeInput': {
                      opacity: 1,
                    },
                  }),
                }}
                {...restProps}
                variant="outlined"
                label={label ?? inputLabel}
                onChange={async (
                  event: SelectChangeEvent<string | number | undefined>,
                ) => {
                  if (confirmation && !!currentValue) {
                    handleOpenConfirmation(event.target.value);
                    return;
                  }
                  onChange(event);
                  await trigger(name);
                  handleChange && handleChange(event);
                }}
                value={
                  // Check if the current value exists in the options
                  value &&
                  options.some(
                    (option) => String(option.value) === String(value),
                  )
                    ? value
                    : ''
                }
              >
                {options.length ? (
                  options.map((o, i) => (
                    <MenuItem
                      data-testid={`adType_${o.value}`}
                      key={`${i}.${o.value}`}
                      value={o.value}
                      sx={{
                        wordBreak: 'break-all',
                        whiteSpace: 'wrap',
                        '&:hover': {
                          backgroundColor: (theme) =>
                            theme.palette.action.hover,
                        },
                        // Optionally, you can add transition for smoothness
                        transition: 'background-color 0.2s',
                        cursor: 'pointer',
                      }}
                      ref={i === options.length - 1 ? loadMoreRef : undefined}
                    >
                      {renderOption ? renderOption(o) : o.label}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No options</MenuItem>
                )}
              </MuiSelect>
              {error?.message && (
                <Typography
                  variant="subtitle1"
                  fontSize={12}
                  sx={{ mt: '3px' }}
                  color="error"
                >
                  {error.message as string}
                </Typography>
              )}
              {!error?.message && helperText && (
                <Typography
                  variant="subtitle1"
                  fontSize={12}
                  sx={{ mt: '3px' }}
                  color="text.secondary"
                >
                  {helperText}
                </Typography>
              )}
              {confirmation && (
                <Dialog
                  fullWidth
                  maxWidth="xs"
                  {...bindDialog(popupState)}
                  onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                      popupState.close();
                    }
                  }}
                >
                  <DialogTitle>{confirmation?.title ?? 'Changing'}</DialogTitle>
                  <DialogContent>
                    <Typography variant="body1">
                      {confirmation?.bodyText ?? 'Are you sure?'}
                    </Typography>
                  </DialogContent>
                  <DialogActions
                    sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}
                  >
                    <Button
                      onClick={() => {
                        popupState.close();
                      }}
                      variant="text"
                      color="error"
                    >
                      {confirmation?.cancelButtonTitle ?? 'Cancel'}
                    </Button>
                    <Button
                      onClick={handleApproveConfirmation}
                      variant="contained"
                    >
                      {confirmation?.submitButtonTitle ?? 'Change'}
                    </Button>
                  </DialogActions>
                </Dialog>
              )}
            </FormControl>
          </Box>
        );
      }}
    />
  );
};

export default Select;
